import BackgroundColors from '@root/joinroot.com/src/utils/background-colors';
import CustomFormBlock from '@root/joinroot.com/src/components/blocks/custom-form-block';
import HeroImageFull from '@root/joinroot.com/src/components/sections/hero-section/hero-section_image-full';
import HeroImageNone from '@root/joinroot.com/src/components/sections/hero-section/hero-section_image-none';
import HeroImageRight from '@root/joinroot.com/src/components/sections/hero-section/hero-section_image-right';
import HeroImageTop from '@root/joinroot.com/src/components/sections/hero-section/hero-section_image-top';
import HomeownersZipCodeForm from '@root/joinroot.com/src/components/forms/homeowners-zip-code-form';
import LinkButton from '@root/joinroot.com/src/models/link-button';
import PropTypes from '@root/vendor/prop-types';
import RentersZipCodeForm from '@root/joinroot.com/src/components/forms/renters-zip-code-form';
import { BRAND_BLING_OPTIONS } from '@root/joinroot.com/src/components/brand-bling';
import { CUSTOM_FORM, HOMEOWNERS_ZIP_CODE_FORM, RENTERS_ZIP_CODE_FORM } from '@root/joinroot.com/src/constants';
import { TEXT_ALIGNMENT_OPTIONS, VERTICAL_IMAGE_ALIGNMENT } from '@root/joinroot.com/src/constants/constants';
import { constantize } from '@root/core/src/utils/strings';

export default function HeroSectionBlock({
  content: hero,
  trackClick,
  trackEvent,
}) {
  const Component = getHeroComponent(hero.imageLocation);
  const FormComponent = buildHeroFormComponent(hero.form);

  function trackCtaClick(btn) {
    trackClick(`CTA_BUTTON_${btn.id}`, {
      buttonText: btn.buttonText,
      buttonLink: btn.buttonLink,
    });
  }

  const analytics = {
    trackClick,
    trackEvent,
  };

  return (
    <Component
      analytics={analytics}
      anchor={hero.anchor?.anchorSlug}
      backgroundColor={BackgroundColors.getDefaultedString(hero.backgroundColor)}
      brandBlingOptions={buildImageOverlay(hero.imageOverlay)}
      buttons={buildCallToActionButtons(hero.callToActionButtons, trackCtaClick)}
      eyebrow={hero.eyebrow}
      form={FormComponent ? (
        <FormComponent
          analytics={analytics}
          content={hero.form}
        />
      ) : null}
      gatsbyImage={hero.asset?.fluid}
      headline={hero.headline}
      hideImageOnMobile={hero.hideHeroImageOnMobile}
      image={hero.asset?.file?.url}
      imageAlt={hero.imageAltText}
      subhead={hero.subhead}
      textAlignment={buildTextAlignment(hero.textAlignment)}
      verticalImageAlignment={buildVerticalImageAlignment(hero.verticalImageAlignment)}
    />
  );
}

HeroSectionBlock.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    anchor: PropTypes.shape({
      id: PropTypes.string,
      anchorSlug: PropTypes.string,
    }),
    eyebrow: PropTypes.string,
    headline: PropTypes.string.isRequired,
    subhead: PropTypes.string,
    imageAltText: PropTypes.string,
    textAlignment: PropTypes.string,
    backgroundColor: PropTypes.string,
    imageLocation: PropTypes.string,
    imageOverlay: PropTypes.string,
    verticalImageAlignment: PropTypes.string,
    hideImageOnMobile: PropTypes.bool,
    asset: PropTypes.shape({
      file: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
    form: PropTypes.shape({
      sys: PropTypes.shape({
        contentType: PropTypes.shape({
          sys: PropTypes.shape({
            id: PropTypes.string.isRequired,
          }).isRequired,
        }).isRequired,
      }).isRequired,
      id: PropTypes.string.isRequired,
    }),
    callToActionButtons: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        buttonType: PropTypes.string.isRequired,
        buttonText: PropTypes.string.isRequired,
        buttonLink: PropTypes.string.isRequired,
      }).isRequired,
    ),
  }).isRequired,
  trackClick: PropTypes.func.isRequired,
  trackEvent: PropTypes.func,
};

function getHeroComponent(imageLocation) {
  const imageLocationToComponent = {
    NO_IMAGE: HeroImageNone,
    TOP_IMAGE: HeroImageTop,
    FULL_BACKGROUND: HeroImageFull,
    RIGHT_IMAGE: HeroImageRight,
    RIGHT_HIDE_IMAGE_ON_MOBILE: HeroImageRight,
  };

  return imageLocationToComponent[constantize(imageLocation)] || HeroImageNone;
}

function buildTextAlignment(textAlignment) {
  const TEXT_ALIGNMENT = {
    CENTER: TEXT_ALIGNMENT_OPTIONS.CENTER,
    LEFT_ALIGN: TEXT_ALIGNMENT_OPTIONS.LEFT,
  };

  return TEXT_ALIGNMENT[constantize(textAlignment)] || TEXT_ALIGNMENT_OPTIONS.CENTER;
}

function buildVerticalImageAlignment(verticalImageAlignment) {
  const IMAGE_ALIGNMENT = {
    CENTER: VERTICAL_IMAGE_ALIGNMENT.CENTER,
    BOTTOM: VERTICAL_IMAGE_ALIGNMENT.BOTTOM,
  };

  return IMAGE_ALIGNMENT[constantize(verticalImageAlignment)] || VERTICAL_IMAGE_ALIGNMENT.CENTER;
}

function buildImageOverlay(imageOverlay) {
  const IMAGE_OVERLAY = {
    NONE: BRAND_BLING_OPTIONS.NONE,
    CARET: BRAND_BLING_OPTIONS.CARET,
    LARGE_ROOT_TEXT: BRAND_BLING_OPTIONS.WORDMARK,
    ROOT_LOGO: BRAND_BLING_OPTIONS.ICON,
  };

  return IMAGE_OVERLAY[constantize(imageOverlay)] || BRAND_BLING_OPTIONS.NONE;
}

function buildCallToActionButtons(buttons = [], trackClick) {
  if (!buttons) { return []; }

  return buttons.map((btn) => ({
    type: LinkButton.types[constantize(btn.buttonType)],
    onClick: () => trackClick(btn),
    text: btn.buttonText,
    link: btn.buttonLink,
  }));
}

function buildHeroFormComponent(form) {
  const typeOfForm = form?.sys?.contentType?.sys?.id;
  const formTypes = {
    [RENTERS_ZIP_CODE_FORM]: RentersZipCodeForm,
    [HOMEOWNERS_ZIP_CODE_FORM]: HomeownersZipCodeForm,
    [CUSTOM_FORM]: CustomFormBlock,
  };

  return formTypes[typeOfForm] || null;
}

export { getHeroComponent, buildImageOverlay, buildCallToActionButtons };
